import { getter } from '@progress/kendo-react-common';
import { Grid, GridColumn as Column, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { groupBy, orderBy, filterBy } from '@progress/kendo-data-query';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import { IntlProvider, load, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import caGregorian from 'cldr-dates-full/main/pt/ca-gregorian.json';
import dateFields from 'cldr-dates-full/main/pt/dateFields.json';
import numbers from 'cldr-numbers-full/main/pt/numbers.json';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { WhiteButton } from '../buttonsComponent';
import ElipseEmpty from '../../assets/elipse-empty.svg';
import ElipseFill from '../../assets/elipse-fill.svg';
import { toMoney } from '../../utils/formatter';
import ptMessages from '../../utils/pt.json';
import { CheckValidDate } from '../../utils/date';
import './style.css';
import { ColumnMenu, ColumnMenuCheckbox } from './columnMenu';
import { TotalCell, CountCell } from '../grid/totalCell';

load(likelySubtags, weekData, caGregorian, dateFields, numbers);
loadMessages(ptMessages, 'pt');

const SELECTED_FIELD = 'selected';

const Table = ({
  name,
  columns,
  data,
  dataItemKey,
  haveCheckbox,
  style,
  setItemKeysSelected,
  onClickInRow,
  sortable,
  filterable,
  rowIndex,
  haveOnClick,
  hasExcel = false
}) => {
  data = data.map((dataItem) => ({
    [SELECTED_FIELD]: false,
    ...dataItem
  }));
  const _export = React.useRef(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };
  const idGetter = getter(dataItemKey);
  const { width } = style;
  const setPercentage = (percentage) => Math.round(width / 100) * percentage;
  const [sort, setSort] = useState([]);
  const [filter, setFilter] = useState(null);
  const [group, setGroup] = useState([]);
  const newSelectedState = {};
  data.forEach((item) => (newSelectedState[idGetter(item)] = false));
  const [selectedState, setSelectedState] = useState(newSelectedState);
  const [_columns, setColumns] = useState(columns);
  const [_filterable, setFilterable] = useState(filterable);
  const [hasFilter, setHasFilter] = useState(false);
  const [hasGroup, setHasGroup] = useState(false);

  const processWithGroups = (data, group, sort, filter, selectedState) => {
    let newDataState = filterBy(data, filter);
    newDataState = newDataState.map((item) => ({
      ...item,
      [SELECTED_FIELD]: selectedState[idGetter(item)]
    }));
    newDataState = orderBy(newDataState, sort);
    newDataState = groupBy(newDataState, group);
    setGroupIds({
      data: newDataState,
      group
    });
    return newDataState;
  };

  const [resultState, setResultState] = useState(
    processWithGroups(data, group, sort, filter, selectedState)
  );

  const [collapsedState, setCollapsedState] = useState([]);

  const objsBooleansToIds = (obj) => {
    const ids = [];
    Object.keys(obj).forEach((key) => {
      if (obj[key]) ids.push(key);
    });
    return ids;
  };

  const onSelectionChange = (event) => {
    const newSelectedState = selectedState;
    if (newSelectedState[event.dataItem.Id] === undefined) {
      newSelectedState[event.dataItem.Id] = true;
    } else {
      newSelectedState[event.dataItem.Id] = !newSelectedState[event.dataItem.Id];
    }
    setItemKeysSelected(objsBooleansToIds(newSelectedState));
    setSelectedState(newSelectedState);
    const newDataState = processWithGroups(data, group, sort, filter, newSelectedState);
    setResultState(newDataState);
  };

  const onHeaderSelectionChange = (event) => {
    const { checked } = event.syntheticEvent.target;
    const { data } = event.target.props;
    const newSelectedState = {};

    data.forEach((item) => {
      newSelectedState[idGetter(item)] = checked;
    });

    setItemKeysSelected(objsBooleansToIds(newSelectedState));
    setSelectedState(newSelectedState);
    const newDataState = processWithGroups(data, group, sort, filter, newSelectedState);
    setResultState(newDataState);
  };

  const defaultCell = ({ dataItem, field }, value) => {
    const valueToShow = value.subField ? dataItem[field][value.subField] : dataItem[field];

    let cellClickHandlerProps = {
      onClick: () => onClickInRow(dataItem, field)
    };
    cellClickHandlerProps = haveOnClick ? cellClickHandlerProps : '';
    let className = '';
    if (value.isThrough) if (dataItem.Through) className = className.concat('line-through-column ');
    if (value.isCash) className = className.concat('cash-column');
    else if (value.isCenter) className = className.concat('center-column');
    else if (value.isRight) className = className.concat('right-column');
    return value.isCash ? (
      <td {...cellClickHandlerProps} className={className}>
        {toMoney(dataItem[field], true)}
      </td>
    ) : (
      <td {...cellClickHandlerProps} className={className}>
        {valueToShow instanceof Date ? valueToShow.toLocaleDateString('pt-BR') : valueToShow}
      </td>
    );
  };

  const changeGridProperty = (gridRef) => {
    let hasTrue = false;
    if (selectedState) {
      hasTrue = Object.values(selectedState).some((value) => value === true);
    }
    if (gridRef && !hasTrue && rowIndex) {
      const { length } = filterBy(data, filter);
      if (length > 0 && rowIndex > length) gridRef.scrollIntoView({ rowIndex: length });
      else gridRef.scrollIntoView({ rowIndex });
    }
  };

  const saveStateToLocalStorage = (filter, sort, group, columns, filterable) => {
    const state = { filter, sort, group, columns, filterable };
    localStorage.setItem(`gridState${name}`, JSON.stringify(state));
  };

  const convertFilter = (filters) => {
    if (filters) {
      const newFilters = filters.map((item) => {
        const date = new Date(item.value);
        if (CheckValidDate(date))
          return {
            ...item,
            value: date
          };
        return item;
      });
      return newFilters;
    }
    return filters;
  };

  const loadStateFromLocalStorage = () => {
    const savedState = localStorage.getItem(`gridState${name}`);
    if (savedState) {
      const {
        filter: savedFilter,
        sort: savedSort,
        group: savedGroup,
        columns: savedColumns,
        filterable: savedFilterable
      } = JSON.parse(savedState);

      if (savedFilter?.filters) {
        savedFilter.filters = savedFilter.filters.map((item) => ({
          ...item,
          filters: convertFilter(item.filters)
        }));
      }

      setFilter(savedFilter);
      if (savedFilter) setHasFilter(true);
      else setHasFilter(false);
      setSort(savedSort);
      setGroup(savedGroup);
      if (savedGroup && savedGroup.length > 0) setHasGroup(true);
      else setHasGroup(false);
      if (savedColumns) {
        const newColumns = _columns.map((item) => {
          const column = savedColumns.find((item2) => item2.field === item.field);
          if (column)
            return {
              ...item,
              width: column.width
            };
          return item;
        });
        setColumns(newColumns);
      }
      setFilterable(savedFilterable);
      const state = processWithGroups(data, savedGroup, savedSort, savedFilter, selectedState);
      setResultState(state);
    }
  };

  const onGroupChange = (event) => {
    const newGroups = event.group;
    const areNewGroupsUnique = !newGroups.some(
      (item, index) => newGroups.findIndex((group) => group.field === item.field) !== index
    );
    if (areNewGroupsUnique) {
      const newDataState = processWithGroups(data, event.group, sort, filter, selectedState);
      setGroup(event.group);
      if (event.group && event.group.length > 0) setHasGroup(true);
      else setHasGroup(false);
      saveStateToLocalStorage(filter, sort, event.group, _columns, _filterable);
      setResultState(newDataState);
    }
  };

  const onExpandChange = useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const newCollapsedIds = !event.value
          ? [...collapsedState, item.groupId]
          : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(newCollapsedIds);
      }
    },
    [collapsedState]
  );

  const onFilterChange = (event) => {
    const newDataState = processWithGroups(data, group, sort, event.filter, selectedState);
    setFilter(event.filter);
    if (event.filter) setHasFilter(true);
    else setHasFilter(false);
    saveStateToLocalStorage(event.filter, sort, group, _columns, _filterable);
    setResultState(newDataState);
  };

  const onSortChange = (event) => {
    const newDataState = processWithGroups(data, group, event.sort, filter, selectedState);
    setSort(event.sort);
    saveStateToLocalStorage(filter, event.sort, group, _columns, _filterable);
    setResultState(newDataState);
  };

  const onColumnResize = (event) => {
    saveStateToLocalStorage(filter, sort, group, event.columns, _filterable);
  };

  const clearFilters = () => {
    onFilterChange({ filter: null });
  };

  const newData = setExpandedState({
    data: resultState,
    collapsedIds: collapsedState
  });

  useEffect(() => {
    loadStateFromLocalStorage();
  }, []);

  return (
    <LocalizationProvider language="pt">
      <IntlProvider locale="pt">
        <ExcelExport data={newData} ref={_export}>
          <Grid
            data={newData}
            dataItemKey={dataItemKey}
            selectedField={SELECTED_FIELD}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            style={style}
            onGroupChange={onGroupChange}
            group={group}
            onExpandChange={onExpandChange}
            expandField="expanded"
            groupable={true}
            sortable={sortable}
            sort={sort}
            onSortChange={onSortChange}
            onFilterChange={onFilterChange}
            filter={filter}
            className="table"
            resizable={true}
            ref={changeGridProperty}
            onColumnResize={onColumnResize}
          >
            {(hasFilter || hasExcel) && (
              <GridToolbar>
                {hasFilter && <WhiteButton onClick={clearFilters}>Limpar Filtro</WhiteButton>}
                {hasExcel && !hasGroup && (
                  <WhiteButton onClick={excelExport}>Exportar para IA</WhiteButton>
                )}
              </GridToolbar>
            )}
            <GridNoRecords>Sem registros</GridNoRecords>
            {haveCheckbox && (
              <Column
                field={SELECTED_FIELD}
                filterable={false}
                width={50}
                headerSelectionValue={
                  data.findIndex((item) => !selectedState[idGetter(item)]) === -1
                }
              />
            )}
            {_columns.map((value, index) => {
              if (value.isRadio) {
                return (
                  <Column
                    field={value.field}
                    title={value.title}
                    format={value.format}
                    width={value.isPercentage ? setPercentage(value.width) : value.width}
                    headerClassName={value.isRadio && 'radio-button'}
                    cell={({ dataItem, field }) => (
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <td
                        className="radio-button"
                        onClick={() => onClickInRow(dataItem)}
                        onKeyPress
                      >
                        {dataItem[field] ? (
                          <img src={ElipseFill} alt="Item preenchido" />
                        ) : (
                          <img src={ElipseEmpty} alt="Item vazio" />
                        )}
                      </td>
                    )}
                    key={`${value.field.toString()}_${index}`}
                    footerCell={value.footerCell}
                  />
                );
              }
              let headerClassName = '';
              if (value.isCash && !value.columnMenu && !value.columnMenuCheckbox)
                headerClassName = 'cash-column';
              else if (value.isCenter && !value.columnMenu && !value.columnMenuCheckbox)
                headerClassName = 'center-column';
              else if (value.isRight && !value.columnMenu && !value.columnMenuCheckbox)
                headerClassName = 'right-column';
              let footerCell = null;
              if (value.totalCell)
                footerCell = (props) => TotalCell(props, filterBy(data, filter), value.field);
              if (value.countCell) footerCell = (props) => CountCell(props, filterBy(data, filter));
              let columnMenu;
              if (value.columnMenu) columnMenu = ColumnMenu;
              if (value.columnMenuCheckbox) {
                columnMenu = (props) => (
                  <ColumnMenuCheckbox {...props} data={orderBy(data, sort)} />
                );
              }
              return (
                <Column
                  field={value.field}
                  title={value.title}
                  filter={value.filter}
                  format={value.format}
                  width={value.isPercentage ? setPercentage(value.width) : value.width}
                  headerClassName={headerClassName}
                  cell={
                    value.cell === undefined ? (props) => defaultCell(props, value) : value.cell
                  }
                  key={`${value.field.toString()}_${index}`}
                  footerCell={footerCell}
                  columnMenu={columnMenu}
                />
              );
            })}
          </Grid>
        </ExcelExport>
      </IntlProvider>
    </LocalizationProvider>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      subField: PropTypes.string,
      title: PropTypes.string,
      width: PropTypes.number,
      isRadio: PropTypes.bool,
      isPercentage: PropTypes.bool,
      isCash: PropTypes.bool
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.any])),
  dataItemKey: PropTypes.string.isRequired,
  style: PropTypes.shape({ subProp: PropTypes.string }),
  haveCheckbox: PropTypes.bool,
  setItemKeysSelected: PropTypes.func,
  onClickInRow: PropTypes.func,
  sortable: PropTypes.bool,
  filterable: PropTypes.bool,
  haveOnClick: PropTypes.bool
};

Table.defaultProps = {
  haveCheckbox: false,
  style: {},
  setItemKeysSelected: () => {
    throw new Error('Not Implemented');
  },
  sortable: false,
  filterable: false,
  haveOnClick: true
};

export default Table;
